import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useWebEDIStore } from 'stores/webedi/webedi';
import { DocumentType, RelationshipV2 } from 'support/types';
import { CreateEditDocumentBottomBanner } from 'pages/Transactions/CreateEditDocumentBottomBanner';
import { DisplayModes } from 'components/DataTable/Types';
import { Form } from 'components/Form/Form';
import { LoadingOverlay } from 'components/Loading/LoadingOverlay';
import { Page, PAGE_SECTION_PORTAL_ROOT_ID } from 'components/Page/Page';
import { Translate } from 'components/Translate/Translate';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';

import { UploadedFileAlert } from './components/UploadedFileAlert';

export type DocumentCreationStepProps = {
  nonVirtualDocumentType: DocumentType;
  relationship: RelationshipV2 | undefined;
  handleOpenConfirmation: () => void;
  uploadedFileName: string | undefined;
  onUploadNewFile: () => void;
  isCreatingDocument: boolean;
};

export const DocumentCreationStep = ({
  nonVirtualDocumentType,
  relationship,
  handleOpenConfirmation,
  uploadedFileName,
  onUploadNewFile,
  isCreatingDocument,
}: DocumentCreationStepProps) => {
  const {
    formState: { errors },
  } = useFormContext();
  const uiConfig = useWebEDIStore((state) => state.uiConfig);

  const errorBannerContent = useMemo(() => {
    if (uploadedFileName) {
      return (
        <div>
          <p className="text-sm font-medium text-gray-900">
            <Translate i18nKey="priceCatalogs:uploaded_error_banner.title" />
          </p>
          <p className="text-sm text-gray-700">
            <Translate i18nKey="priceCatalogs:uploaded_error_banner.description" />
          </p>
        </div>
      );
    }
  }, [uploadedFileName]);
  return (
    <>
      <Page.Section
        topBanner={
          Object.keys(errors).length ? (
            <WebEDIMessageErrorBanner mode={DisplayModes.create} blockingError={null} content={errorBannerContent} />
          ) : null
        }
        bottomBanner={<CreateEditDocumentBottomBanner documentType={nonVirtualDocumentType} />}
      >
        <div className="flex flex-col gap-4">
          <UploadedFileAlert uploadedFileName={uploadedFileName} onUploadNewFile={onUploadNewFile} />
          <Form
            noValidate={true}
            onSubmit={handleOpenConfirmation}
            id="webedi-document"
            data-testid="webedi-document"
            autoComplete="off"
          >
            {uiConfig ? (
              <DocumentSections
                mode={DisplayModes.create}
                uiConfig={uiConfig}
                documentType={nonVirtualDocumentType}
                variables={
                  relationship?.receiver_partner ? { receiverPartnerName: relationship.receiver_partner.name } : {}
                }
                isSubmitting={isCreatingDocument}
                senderPartner={relationship?.sender_partner}
                receiverPartner={relationship?.receiver_partner}
              />
            ) : null}
          </Form>
        </div>
        <LoadingOverlay isVisible={isCreatingDocument} portalRootId={PAGE_SECTION_PORTAL_ROOT_ID} />
      </Page.Section>
    </>
  );
};
